import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { isFulfilled } from '@reduxjs/toolkit';

import { Button, Modal, Text } from '@auspost/postmaster-react';

import { trackInteraction } from 'store/analytics/analytics.thunks';
import { deleteShipments } from 'store/shipment/details/shipmentDetails.slice';
import { useAppDispatch, useAppSelector } from 'store/store';

import { trackUiAppId } from '../../../../../config/track-config';
import { NotificationStatus, NotificationType } from '../../../../../models/Notification';
import { postMxNotificationMsg } from '../../../../../utils/message-exchange.utils';
import { successContent } from '../../../../common/Notification/Notification.config';
import { useModalBodyOverflow } from '../../../../hooks/modalBodyOverflow.hooks';
import { useCurrentArticle, useShipment } from '../../../hooks/shipment.hooks';
import { REMOVE_ITEM_CANCEL_BTN_TEST_ID, REMOVE_ITEM_REMOVE_BTN_TEST_ID } from '../RemoveOptions.test-ids';

import styles from './RemoveItemModal.module.scss';
interface RemoveItemModalProp {
  isOpen: boolean;
  handleClose: () => void;
}

const RemoveItemModal: React.FC<RemoveItemModalProp> = ({ isOpen, handleClose }) => {
  const { trackingId } = useParams();
  const articleId = useCurrentArticle()?.articleId ?? '';
  const consignmentId = useShipment()?.consignmentId ?? '';
  const nickname = useCurrentArticle()?.nickname ?? null;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isLoading = useAppSelector((state) => state.shipmentDetails.deleteShipment)?.loading;
  const { resetBodyOverflow } = useModalBodyOverflow(isOpen); // Use custom hook to manage body overflow

  const handleRemove = async () => {
    if (!articleId) return null;
    dispatch(trackInteraction('remove the article'));
    const deleteAction = await dispatch(
      deleteShipments({ trackingIds: [articleId], consignmentIds: [consignmentId], nickname })
    );
    const successRemovePayload = {
      type: NotificationType.ALERT,
      status: NotificationStatus.SUCCESS,
      message: successContent.removeArticleFromDetails([articleId], nickname),
    };

    if (isFulfilled(deleteAction)) {
      handleClose();
      resetBodyOverflow(); // Reset body overflow before navigation
      postMxNotificationMsg(successRemovePayload, trackUiAppId);
      navigate('/search');
    }
  };
  if (!trackingId) return null;

  return (
    <Modal
      isModalShowing={isOpen}
      onHide={handleClose}
      modalClassName={styles.removeItemModal}
      dialogClassName={styles.removeItemDialog}
      ariaDialogLabel='remove item modal'
      dialogId='remove item modal'
    >
      <Modal.Header closeButton={false} className={styles.removeItemModalHeader}>
        <h1 className={styles.removeItemModalHeading}>Remove from track list?</h1>
      </Modal.Header>
      <Modal.Content>
        <Text variant='body'>We&apos;ll also stop sending you any notifications for this parcel.</Text>
      </Modal.Content>
      <Modal.Footer alignment='center'>
        <Button
          size='medium'
          variant='secondary'
          className={styles.removeItemModalButton}
          onClick={handleClose}
          data-testid={REMOVE_ITEM_CANCEL_BTN_TEST_ID}
        >
          Cancel
        </Button>
        <Button
          size='medium'
          variant='primary'
          className={styles.removeItemModalButton}
          isLoading={isLoading}
          onClick={handleRemove}
          data-testid={REMOVE_ITEM_REMOVE_BTN_TEST_ID}
        >
          Remove
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RemoveItemModal;
